import React from "react";
import { useNavigate } from "react-router-dom";

interface ServiceFeature {
  title: string;
  description: string;
  icon: JSX.Element;
}

const InstallationServices: React.FC = () => {
  const navigate = useNavigate();

  const features: ServiceFeature[] = [
    {
      title: "Equipment Installation",
      description:
        "Professional installation of industrial equipment and machinery with precision and safety.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
    {
      title: "System Integration",
      description:
        "Seamless integration of new equipment with existing systems and infrastructure.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
      ),
    },
    {
      title: "Testing & Commissioning",
      description:
        "Comprehensive testing and commissioning services to ensure optimal performance.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const services = [
    {
      title: "Pre-Installation Services",
      items: [
        "Site Assessment",
        "Layout Planning",
        "Infrastructure Preparation",
        "Safety Planning",
      ],
    },
    {
      title: "Installation Process",
      items: [
        "Equipment Assembly",
        "Mechanical Installation",
        "Electrical Integration",
        "Control System Setup",
      ],
    },
    {
      title: "Post-Installation",
      items: [
        "System Testing",
        "Performance Verification",
        "Operator Training",
        "Documentation",
      ],
    },
  ];

  const expertise = [
    "Manufacturing Equipment",
    "Processing Lines",
    "Material Handling Systems",
    "Industrial Machinery",
    "Control Systems",
    "Safety Equipment",
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <img
            src="/about.jpg"
            alt="Installation Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black opacity-60"></div>
        </div>
        <div className="relative container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">
            Equipment Installation Services
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Professional installation services for industrial equipment and
            machinery. We ensure safe, efficient, and reliable setup of your
            systems.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-orange-500 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Installation Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-orange-500">
                  {service.title}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold mb-6">Areas of Expertise</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-600 mb-4">
                  Our installation expertise covers a wide range of industrial
                  equipment:
                </p>
                <ul className="space-y-3">
                  {expertise.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-orange-500 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <img
                  src="/quality.jpg"
                  alt="Installation Expertise"
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Need Professional Installation Services?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your equipment installation needs. Our
            experienced team ensures smooth and efficient implementation.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate("/contacts")}
              className="bg-white text-orange-500 px-8 py-3 rounded hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </button>
            <button
              onClick={() => navigate("/projects")}
              className="border-2 border-white text-white px-8 py-3 rounded hover:bg-white hover:text-orange-500 transition-colors"
            >
              View Projects
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstallationServices;
